import { Stack, Tooltip } from "@mui/material";
import { StatusChip, StatusChipColor, StatusChipVariant } from "components/StatusChip";
import { FindingSeverity } from "generated-graphql/graphql";

export type EntityFinding = {
  id: string;
  severity: FindingSeverity;
  title: string;
  detail: string;
};

const SEVERITY_COLORS: Record<FindingSeverity, StatusChipColor> = {
  [FindingSeverity.High]: "error",
  [FindingSeverity.Medium]: "warning",
  [FindingSeverity.Low]: "info",
};

/**
 * A compliance finding shown inline on the record it is scoped to. Reads the
 * same findings the dashboard feed does, so a badge and its dashboard row can
 * never disagree.
 *
 * The label is the finding's title; the prose `detail` goes in a tooltip rather
 * than being crammed into the chip.
 */
export function ComplianceFindingBadge({
  finding,
  variant,
}: {
  finding: EntityFinding;
  variant?: StatusChipVariant;
}) {
  return (
    <Tooltip title={finding.detail}>
      <span>
        <StatusChip
          color={SEVERITY_COLORS[finding.severity]}
          variant={variant}
          label={finding.title}
        />
      </span>
    </Tooltip>
  );
}

/** All of a record's findings, most severe first. Renders nothing when clear. */
export function ComplianceFindingBadges({
  findings,
  variant,
}: {
  findings: readonly EntityFinding[];
  variant?: StatusChipVariant;
}) {
  if (findings.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {findings.map((finding) => (
        <ComplianceFindingBadge key={finding.id} finding={finding} variant={variant} />
      ))}
    </Stack>
  );
}
